import { employmentRoleFromDesignation } from "constants/adminEmployeeMgmt.constant";

function parseApiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.detail === "string" && data.detail.trim()) {
      return data.detail.trim();
    }
    if (Array.isArray(data.detail) && data.detail.length) {
      return data.detail.map((x) => String(x)).join(" ");
    }
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
  }
  const msg = error?.message;
  if (typeof msg === "string" && msg.trim() && !/^AxiosError/i.test(msg)) {
    return msg.trim();
  }
  return fallback;
}

/** Profile `module` → sidebar `nav.key` / route `key` */
export const PROFILE_MODULE_TO_NAV_KEY = {
  dashboard: "dashboard",
  orders: "orders",
  order: "orders",
  order_report: "order-report",
  "order-report": "order-report",
  // Legacy Technology matrix (permission.map.js)
  "technology.order_report": "order-report",

  shipments: "shipments",
  shipment: "shipments",
  seller: "seller",
  sellers: "seller",
  seller_login: "seller-login",
  "seller-login": "seller-login",
  crm: "seller",
  Esclation: "Esclation",
  escalation: "Esclation",
  cod_remittance: "cod-remittance",
  "cod-remittance": "cod-remittance",
  cod: "cod-remittance",
  weight: "weight",
  dispute_records: "dispute-records",
  "dispute-records": "dispute-records",
  "dispute_records.create": "dispute-records",
  "dispute_records.update": "dispute-records",
  "weight.dispute": "dispute-records",
  serviceability: "serviceability",
  serviceable_pincode: "serviceability",
  "technology.serviceable_pincode": "serviceability",
  admin_user: "admin-user-team",
  employee: "admin-user-team",
  team: "admin-user-team",
};

/** Permission codes that grant page/menu view access */
export const VIEW_GRANTING_CODES = new Set([
  "view",
  "read",
  "list",
  "access",
  "create",
  "manage",
  "edit",
  "update",
  "delete",
]);

export function getProfileApiErrorMessage(
  error,
  fallback = "Could not load your profile. Please try again."
) {
  const status = error?.response?.status;
  let message = parseApiErrorMessage(error, fallback);

  if (status === 401) {
    message = "Your session has expired. Please sign in again.";
  } else if (status === 403) {
    message = "You do not have permission to access this account.";
  } else if (status === 404) {
    message = "Profile not found.";
  } else if (
    !error?.response &&
    String(error?.message || "")
      .toLowerCase()
      .includes("network")
  ) {
    message = "Network error. Check your connection and try again.";
  }

  return message;
}

/** Normalize GET /admin-employee/profile/ permissions → Redux permission shape */
export function normalizeProfilePermissions(rawPermissions = []) {
  if (!Array.isArray(rawPermissions)) return [];

  return rawPermissions.map((perm) => {
    const module = String(perm.module || perm.module_code || "")
      .toLowerCase()
      .trim();
    const code = String(perm.code || perm.action || "view")
      .toLowerCase()
      .trim();
    const permission_key = perm.permission_key || `${module}.${code}`;
    const grantsView =
      perm.can_view === true ||
      (perm.can_view !== false && VIEW_GRANTING_CODES.has(code));

    return {
      id: perm.id,
      permission_key,
      module,
      code,
      name: perm.name || "",
      description: perm.description || "",
      module_name: perm.module || perm.module_name || module,
      module_code: module,
      can_view: grantsView,
      can_create:
        perm.can_create === true ||
        ["create", "add", "write"].includes(code),
      can_update:
        perm.can_update === true ||
        ["update", "edit", "modify"].includes(code),
      nav_key:
        PROFILE_MODULE_TO_NAV_KEY[module] ||
        PROFILE_MODULE_TO_NAV_KEY[String(permission_key).toLowerCase()] ||
        null,
    };
  });
}

export function resolveDesignationFromProfile(profile) {
  if (!profile) return null;
  return (
    employmentRoleFromDesignation(profile.designation) ||
    profile.employment_role ||
    null
  );
}

export function isAdminEmployeeUser(user = {}) {
  if (!user || user.is_superuser) return false;
  return Boolean(
    user.is_admin_employee ||
      user.user_type === "admin_employee" ||
      user.designation ||
      resolveDesignationFromProfile(user)
  );
}

export function getAllowedNavKeysFromPermissions(permissions = []) {
  const keys = new Set();
  permissions.forEach((perm) => {
    if (perm?.can_view !== true) return;
    if (perm.nav_key) keys.add(perm.nav_key);
    const mapped = PROFILE_MODULE_TO_NAV_KEY[String(perm.module || "").toLowerCase()];
    if (mapped) keys.add(mapped);
    const byPermissionKey =
      PROFILE_MODULE_TO_NAV_KEY[
        String(perm.permission_key || "").toLowerCase()
      ];
    if (byPermissionKey) keys.add(byPermissionKey);
  });
  return keys;
}

function matchesProfileModule(permissions, profileModule) {
  const modules = (
    Array.isArray(profileModule) ? profileModule : [profileModule]
  ).map((m) => String(m).toLowerCase());

  return permissions.some((perm) => {
    if (perm?.can_view !== true) return false;
    const permModule = String(perm.module || perm.module_code || "").toLowerCase();
    return modules.includes(permModule);
  });
}

function matchesLegacyPermissionKey(permissions, permissionKey) {
  if (!permissionKey) return false;
  const targets = (
    Array.isArray(permissionKey) ? permissionKey : [permissionKey]
  ).map((k) => String(k).toLowerCase());

  return permissions.some((perm) => {
    if (perm?.can_view !== true) return false;
    const key = String(
      perm.permission_key || `${perm.module || ""}.${perm.code || ""}`
    ).toLowerCase();
    if (targets.includes(key)) return true;
    // Accept module-only keys like "order_report" / "order-report"
    const module = String(perm.module || perm.module_code || "").toLowerCase();
    if (targets.some((t) => t === module || t.endsWith(`.${module}`))) {
      return true;
    }
    // Accept suffix match: *.order_report
    return targets.some((t) => {
      const leaf = t.includes(".") ? t.split(".").pop() : t;
      return key === leaf || key.endsWith(`.${leaf}`) || module === leaf;
    });
  });
}

/** Order Report visible in production for users with orders / order_report access */
function canAccessOrderReport(permissions = []) {
  if (!Array.isArray(permissions) || permissions.length === 0) return false;

  if (matchesProfileModule(permissions, ["order_report", "orders", "order"])) {
    return true;
  }
  if (
    matchesLegacyPermissionKey(permissions, [
      "technology.order_report",
      "order_report",
      "order_report.view",
      "employee_panel.order_report",
    ])
  ) {
    return true;
  }

  const allowed = getAllowedNavKeysFromPermissions(permissions);
  return allowed.has("order-report") || allowed.has("orders");
}

function isOrderReportItem(item) {
  const path = String(item?.path || "")
  return (
    item?.key === "order-report" ||
    item?.key === "order-report-generated" ||
    path === "/order-report" ||
    path === "/order-report/generated" ||
    path.startsWith("/order-report/")
  )
}

/** Dispute Records — public for every logged-in user */
function canAccessDisputeRecords() {
  return true;
}

function isDisputeRecordsItem(item) {
  return (
    item?.key === "dispute-records" || item?.path === "/dispute-records"
  );
}

/** Serviceability — visible for logged-in users (same as Dispute Records). */
function canAccessServiceability() {
  return true;
}

function isServiceabilityItem(item) {
  return item?.key === "serviceability" || item?.path === "/serviceability";
}

function passesDesignationGate(navItem, designation) {
  if (!navItem?.designationAccess?.length) return true;
  const role =
    employmentRoleFromDesignation(designation) ||
    String(designation || "").toLowerCase();
  return navItem.designationAccess.includes(role);
}

/**
 * Admin-employee menu visibility: profile permissions + designation.
 * Returns null when legacy permission-key logic should run.
 */
export function isAdminEmployeeNavVisible(
  navItem,
  userPermissions = [],
  { designation, isSuperuser } = {}
) {
  if (isSuperuser) return true;
  if (!navItem) return false;

  // Parent containers (e.g. NAV_ITEM_TYPE_TITLE) — children decide visibility
  if (navItem.subMenu?.length > 0) {
    return null;
  }

  // Production + local: Order Report for users who can access Orders / order_report
  if (isOrderReportItem(navItem)) {
    return canAccessOrderReport(userPermissions);
  }

  // Dispute Records for users with dispute / shipment / order access
  if (isDisputeRecordsItem(navItem)) {
    return canAccessDisputeRecords(userPermissions);
  }

  if (isServiceabilityItem(navItem)) {
    return canAccessServiceability(userPermissions);
  }

  if (!passesDesignationGate(navItem, designation)) {
    return false;
  }

  if (navItem.profileModule || navItem.permissionKey) {
    const byModule = navItem.profileModule
      ? matchesProfileModule(userPermissions, navItem.profileModule)
      : false;
    const byKey = matchesLegacyPermissionKey(
      userPermissions,
      navItem.permissionKey
    );
    if (byModule || byKey) return true;
    if (navItem.key && userPermissions.length > 0) {
      const allowed = getAllowedNavKeysFromPermissions(userPermissions);
      if (allowed.has(navItem.key)) return true;
    }
    // Both gates present and neither matched — hide
    if (navItem.profileModule && navItem.permissionKey) return false;
    if (navItem.profileModule) return false;
    // Only permissionKey — fall through to legacy isMenuVisible
    return null;
  }

  if (navItem.designationAccess?.length && !navItem.permissionKey) {
    return true;
  }

  if (navItem.key && userPermissions.length > 0) {
    const allowed = getAllowedNavKeysFromPermissions(userPermissions);
    if (allowed.has(navItem.key)) return true;
  }

  if (userPermissions.length > 0 && !navItem.permissionKey) {
    return false;
  }

  return null;
}

export function canAccessRouteByProfile(
  route,
  { permissions = [], designation, isSuperuser, isAdminEmployee } = {}
) {
  if (isSuperuser) return true;
  if (!isAdminEmployee) return null;

  if (isOrderReportItem(route)) {
    return canAccessOrderReport(permissions);
  }

  if (isDisputeRecordsItem(route)) {
    return canAccessDisputeRecords(permissions);
  }

  if (isServiceabilityItem(route)) {
    return canAccessServiceability(permissions);
  }

  if (!route?.permissionKey && !route?.profileModule && !route?.designationAccess) {
    return true;
  }

  if (!passesDesignationGate(route, designation)) {
    return false;
  }

  if (route.profileModule || route.permissionKey) {
    const byModule = route.profileModule
      ? matchesProfileModule(permissions, route.profileModule)
      : false;
    const byKey = matchesLegacyPermissionKey(permissions, route.permissionKey);
    if (byModule || byKey) return true;
    if (route.key && permissions.length > 0) {
      const allowed = getAllowedNavKeysFromPermissions(permissions);
      if (allowed.has(route.key)) return true;
    }
    if (route.profileModule && route.permissionKey) return false;
    if (route.profileModule) return false;
    return null;
  }

  if (route.designationAccess?.length && !route.permissionKey) {
    return true;
  }

  if (route.key && permissions.length > 0) {
    const allowed = getAllowedNavKeysFromPermissions(permissions);
    if (allowed.has(route.key)) return true;
  }

  if (!route.permissionKey && !route.profileModule) {
    return permissions.length === 0;
  }

  return null;
}

function pickProfilePersonName(ref) {
  if (!ref || typeof ref !== "object") return null;
  const combined = [ref.first_name, ref.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  const full =
    String(ref.full_name || ref.name || combined || ref.email || "").trim();
  return full || null;
}

/** Normalize manager / HOD objects from GET /admin-employee/profile/ */
export function normalizeProfilePersonRef(ref) {
  if (!ref || typeof ref !== "object") return null;
  const fullName = pickProfilePersonName(ref);
  if (!fullName && !ref.email) return null;
  return {
    id: ref.id ?? ref.user_id ?? null,
    fullName: fullName || String(ref.email || "").trim(),
    email: ref.email ? String(ref.email).trim() : null,
    department: ref.department ? String(ref.department).trim() : null,
    first_name: ref.first_name ?? null,
    last_name: ref.last_name ?? null,
  };
}

/**
 * Reporting lines for profile UI: direct manager first, then department HOD(s).
 */
export function getProfileReportingContacts(profile) {
  const manager = normalizeProfilePersonRef(profile?.manager);

  const hods = (Array.isArray(profile?.hods) ? profile.hods : [])
    .map(normalizeProfilePersonRef)
    .filter(Boolean);

  const flatManagerName = String(
    profile?.manager_name || profile?.reports_to_name || ""
  ).trim();
  const flatManagerEmail = String(profile?.manager_email || "").trim();
  const managerResolved =
    manager ||
    (flatManagerName
      ? {
          id: profile?.manager_id ?? profile?.reports_to_manager_id ?? null,
          fullName: flatManagerName,
          email: flatManagerEmail || null,
          department: null,
          first_name: null,
          last_name: null,
        }
      : null);

  const flatHodName = String(
    profile?.hod_name || profile?.head_of_department_name || ""
  ).trim();
  let hodsResolved = hods;
  if (!hodsResolved.length && flatHodName) {
    hodsResolved = [
      {
        id: profile?.hod_id ?? profile?.head_of_department_id ?? null,
        fullName: flatHodName,
        email: profile?.hod_email ? String(profile.hod_email).trim() : null,
        department: profile?.department
          ? String(profile.department).trim()
          : null,
        first_name: null,
        last_name: null,
      },
    ];
  }

  return {
    manager: managerResolved,
    hods: hodsResolved,
  };
}

/** Build Redux user payload from GET /admin-employee/profile/ */
export function buildUserFromAdminEmployeeProfile(profile, currentUser = {}) {
  const permissions = normalizeProfilePermissions(profile?.permissions);
  const designation =
    profile?.designation || currentUser.designation || null;
  const employment_role = resolveDesignationFromProfile(profile);

  return {
    ...currentUser,
    id: profile?.id ?? profile?.user_id ?? currentUser.id,
    email: profile?.email || currentUser.email,
    first_name: profile?.first_name ?? currentUser.first_name,
    last_name: profile?.last_name ?? currentUser.last_name,
    userName:
      profile?.full_name ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
      currentUser.userName,
    designation,
    department: profile?.department ?? currentUser.department,
    is_active: profile?.is_active ?? currentUser.is_active,
    permissions,
    roles: profile?.roles || currentUser.roles || [],
    hods: profile?.hods || currentUser.hods || [],
    manager: profile?.manager ?? currentUser.manager,
    is_admin_employee: true,
    user_type: "admin_employee",
    ...(employment_role ? { employment_role } : {}),
    is_hod: employment_role === "hod",
    is_manager: employment_role === "manager",
    profile_loaded: true,
    profile_error: null,
  };
}
